import React from 'react';
import { I18nManager, Linking, Platform } from 'react-native';
import { t } from '@lingui/macro';
import { useNavigation } from '@react-navigation/native';

import GetImmigrationPlans from '@src/api/GetImmigrationPlans';
import PayForImmigrationPlan from '@src/api/PayForImmigrationPlan';
import { generateImmigrationPlan } from '@src/factories';
import useAxiosErrorHandler from '@src/hooks/useAxiosErrorHandler';
import useAxiosRequestConfig from '@src/hooks/useAxiosRequestConfig';
import useIsMounted from '@src/hooks/useIsMounted';
// import useLabelsFilled from '@src/hooks/useLabelsFilled';
import useRefreshImmigrationStatus from '@src/hooks/useRefreshImmigrationStatus';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n } from '@src/i18n';
import { ImmigrationPlan } from '@src/types';

const usePayForImmigrationPlan = () => {
  const axiosRequestConfig = useAxiosRequestConfig();
  const axiosErrorHandler = useAxiosErrorHandler();
  const isMounted = useIsMounted();

  const [loading, setLoading] = React.useState(false);

  const makeRequest = React.useCallback(
    async (
      id: string,
      immigrationId?: string
    ): Promise<{ status: number; link?: string }> => {
      try {
        setLoading(true);
        const response = await PayForImmigrationPlan(
          id,
          immigrationId,
          axiosRequestConfig
        );
        if (
          response.data.paymentStatus === 'NO_BALANCE' &&
          response.data.redirect.type === 'EXTERNAL'
        ) {
          return { link: response.data.redirect.url, status: 302 };
        } else if (response.data.paymentStatus === 'SUCCESS') {
          return { status: 200 };
        } else {
          return { status: 520 };
        }
      } catch (e) {
        return axiosErrorHandler(e);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    },
    [axiosErrorHandler, axiosRequestConfig, isMounted]
  );

  return React.useMemo(() => ({ makeRequest, loading, setLoading }), [
    loading,
    makeRequest,
  ]);
};

const usePlans = () => {
  const axiosRequestConfig = useAxiosRequestConfig();
  const axiosErrorHandler = useAxiosErrorHandler();
  const [data, setData] = React.useState<ImmigrationPlan[]>([]);
  const [loading, setLoading] = React.useState(true);
  const isMounted = useIsMounted();

  const makeRequest = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await GetImmigrationPlans(0, 10, axiosRequestConfig);
      setData(
        response.data.plans
          .filter((e) => e.planType === 'PRIME_PLUS')
          .map((item) => generateImmigrationPlan(item))
      );
      setLoading(false);
    } catch (e) {
      if (isMounted) {
        setLoading(false);
      }
      axiosErrorHandler(e);
    }
  }, [axiosErrorHandler, axiosRequestConfig, isMounted]);

  React.useEffect(() => {
    makeRequest().catch(console.log);
  }, [makeRequest]);

  return React.useMemo(() => ({ data, loading }), [data, loading]);
};

const useBuyImmigrationPlans = (
  immigrationId?: string,
  result?: 'ok' | 'fail'
) => {
  //const labelsFilled = useLabelsFilled();
  const plans = usePlans();
  const payForImmigrationPlan = usePayForImmigrationPlan();
  const refreshImmigrationStatus = useRefreshImmigrationStatus(true);
  const isMounted = useIsMounted();

  const snackBar = useSnackBar();
  const navigation = useNavigation();
  const [activePlanIndex, setActivePlanIndex] = React.useState(0);
  const activePlan = React.useMemo(() => plans.data[activePlanIndex], [
    activePlanIndex,
    plans.data,
  ]);

  const resolveBuyPlanResult = React.useCallback(
    async (status: number, link?: string) => {
      if (status === 200) {
        snackBar.show({
          content: i18n._(t`Immigration subscription activated`),
        });
        // if (labelsFilled) {
        navigation.navigate('ImmigrationHome');
        // }
      } else if (status === 302) {
        const url = link;
        if (url && (await Linking.canOpenURL(url))) {
          await Linking.openURL(url);
        } else {
          snackBar.show({ content: i18n._(t`Can't open link`) });
        }
      } else {
        snackBar.show({ content: i18n._(t`Unknown payment status`) });
      }
    },
    [
      //labelsFilled,
      navigation,
      snackBar,
    ]
  );

  const handleBuyPress = React.useCallback(async () => {
    if (!activePlan?.id) {
      return;
    }
    const response = await payForImmigrationPlan.makeRequest(
      activePlan.id,
      immigrationId
    );
    await resolveBuyPlanResult(response.status, response.link);
  }, [activePlan, payForImmigrationPlan, immigrationId, resolveBuyPlanResult]);

  const handleBuyPlan = React.useCallback(
    async (activePlanId: string) => {
      const response = await payForImmigrationPlan.makeRequest(
        activePlanId,
        immigrationId
      );
      await resolveBuyPlanResult(response.status, response.link);
    },
    [payForImmigrationPlan, immigrationId, resolveBuyPlanResult]
  );

  const calculateIndex = React.useCallback(
    (index: number) =>
      Platform.OS === 'android'
        ? I18nManager.isRTL
          ? index
          : plans.data.length - index - 1
        : index,
    [plans.data.length]
  );

  const handleSnapToPlan = React.useCallback(
    (index: number) => {
      setActivePlanIndex(calculateIndex(index));
    },
    [calculateIndex]
  );

  React.useEffect(() => {
    if (isMounted) {
      if (result === 'ok') {
        snackBar.show({
          content: i18n._(t`Immigration subscription activated`),
        });
        navigation.setParams({ result: undefined });
        refreshImmigrationStatus()
          .then(() => {
            // if (labelsFilled) {
            immigrationId
              ? // @ts-ignore
                navigation.replace('ImmigrationBook', { id: immigrationId })
              : // @ts-ignore
                navigation.replace('ImmigrationHome');
            // }
          })
          .catch(console.log);
      } else if (result === 'fail') {
        snackBar.show({ content: i18n._(t`Unsuccessful payment`) });
        payForImmigrationPlan.setLoading(false);
        navigation.setParams({ result: undefined });
      }
    }
  }, [
    isMounted,
    // labelsFilled,
    navigation,
    payForImmigrationPlan,
    immigrationId,
    refreshImmigrationStatus,
    result,
    snackBar,
  ]);

  React.useEffect(() => {
    if (!plans.loading && isMounted) {
      setActivePlanIndex(Math.floor(plans.data.length / 2));
    }
  }, [isMounted, plans.data.length, plans.loading]);

  return React.useMemo(
    () => ({
      activePlanIndex,
      plans,
      handleSnapToPlan,
      handleBuyPress,
      payForImmigrationPlan,
      calculateIndex,
      activePlan,
      handleBuyPlan,
    }),
    [
      activePlan,
      activePlanIndex,
      calculateIndex,
      handleBuyPress,
      handleSnapToPlan,
      payForImmigrationPlan,
      plans,
      handleBuyPlan,
    ]
  );
};

export default useBuyImmigrationPlans;
